// Controller for <bundle-save-qty-1-step-component>
// - independent per-variant steppers
// - total quantity drives tier pricing + gift unlocks + CTA state
// - subscription-only; checkout adds each variant with its selling plan

// ebook + mystery unlock at 3, shipping at 5, bottle at 6
const GIFT_UNLOCKS = {
  ebook: 3,
  mystery: 3,
  shipping: 5,
  bottle: 6,
};
const SHIPPING_UNLOCK_AT = 5;

// Hardcoded to mirror OG bundle-save.js — template "$8.31/ea" etc. are display strings, not math inputs
const TIER_PRICES = { 3: 9.99, 6: 9.69, 9: 8.97 };      // $/ea at each tier
const RETAIL_TOTALS = { 3: 79.65, 6: 147.45, 9: 198.30 }; // bag+shipping+gifts at exact tier qty
const MAX_BAG_PRICE = 16.95;

// Free-gift variant IDs (mirrors OG bundle-save.js). Shipping isn't a product — it's handled by the discount code.
const GIFT_VARIANT_IDS = {
  ebook: '44629507899574',   // unlock at 3
  mystery: '44629518287030', // unlock at 3
  bottle: '44505312460982',  // unlock at 6 (wintergreen)
};
const CHECKOUT_DISCOUNT_CODE = 'WELCOME';

const formatMoney = (n) => `$${n.toFixed(2)}`;

class BundleSaveQty1StepComponent extends HTMLElement {
  #buildingCountdownInterval = null;

  connectedCallback() {
    this.minQuantity = parseInt(this.dataset.minQuantity || '3', 10);
    this.sellingPlanId = this.dataset.sellingPlanId || '';

    this.quantities = new Map();
    this.querySelectorAll('[data-variant-id]').forEach((row) => {
      this.quantities.set(row.dataset.variantId, 0);
    });

    this.#bindEvents();
    this.#setupStickyObserver();
    this.refresh();

    // Port of OG bfcache reset: clear the building countdown, hide loading state, re-sync UI.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        if (this.#buildingCountdownInterval) {
          clearInterval(this.#buildingCountdownInterval);
          this.#buildingCountdownInterval = null;
        }
        this.#hideLoadingState();
        this.refresh();
      }
    });
  }

  // Port of OG #showLoadingState/#startBuildingCountdown:
  // - add .is-loading
  // - disable button
  // - show loader icon
  // - animate text "BUILDING BUNDLE" with cycling 1-3 dots
  #showLoadingState() {
    const cta = this.querySelector('[data-cta]');
    const ctaText = this.querySelector('[data-cta-text]');
    const ctaLoader = this.querySelector('[data-cta-loader]');
    const stickyCta = this.querySelector('[data-sticky-cta]');
    const stickyCtaText = this.querySelector('[data-sticky-cta-text]');
    const stickyCtaLoader = this.querySelector('[data-sticky-cta-loader]');

    if (cta) {
      cta.classList.add('is-loading');
      cta.setAttribute('disabled', 'disabled');
    }
    if (ctaLoader instanceof HTMLElement) ctaLoader.style.display = 'inline-flex';
    if (ctaText instanceof HTMLElement) ctaText.textContent = 'BUILDING BUNDLE...';

    if (stickyCta) {
      stickyCta.classList.add('is-loading');
      stickyCta.setAttribute('disabled', 'disabled');
    }
    if (stickyCtaLoader instanceof HTMLElement) stickyCtaLoader.style.display = 'inline-flex';
    if (stickyCtaText instanceof HTMLElement) stickyCtaText.textContent = 'BUILDING BUNDLE...';

    if (this.#buildingCountdownInterval) clearInterval(this.#buildingCountdownInterval);
    let tick = 0;
    this.#buildingCountdownInterval = setInterval(() => {
      tick++;
      const dots = '.'.repeat((tick % 3) + 1);
      const label = `BUILDING BUNDLE${dots}`;
      if (ctaText instanceof HTMLElement) ctaText.textContent = label;
      if (stickyCtaText instanceof HTMLElement) stickyCtaText.textContent = label;
    }, 500);
  }

  // Port of OG #hideLoadingState:
  // - clear the dot countdown
  // - remove .is-loading + disabled
  // - hide loader icon (refresh() will restore correct text)
  #hideLoadingState() {
    if (this.#buildingCountdownInterval) {
      clearInterval(this.#buildingCountdownInterval);
      this.#buildingCountdownInterval = null;
    }
    const cta = this.querySelector('[data-cta]');
    const ctaLoader = this.querySelector('[data-cta-loader]');
    const stickyCta = this.querySelector('[data-sticky-cta]');
    const stickyCtaLoader = this.querySelector('[data-sticky-cta-loader]');

    if (cta) {
      cta.classList.remove('is-loading');
      cta.removeAttribute('disabled');
    }
    if (ctaLoader instanceof HTMLElement) ctaLoader.style.display = 'none';
    if (stickyCta) {
      stickyCta.classList.remove('is-loading');
      stickyCta.removeAttribute('disabled');
    }
    if (stickyCtaLoader instanceof HTMLElement) stickyCtaLoader.style.display = 'none';
  }

  #setupStickyObserver() {
    const mainCta = this.querySelector('[data-cta]');
    const sticky = this.querySelector('[data-sticky]');
    if (!mainCta || !sticky) return;
    sticky.hidden = false; // allow visibility toggling via data attribute
    // Show sticky only once the main CTA has scrolled ABOVE the viewport — not when
    // it's below (i.e., not yet reached). Matches V1 page behavior.
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const scrolledPast = !e.isIntersecting && e.boundingClientRect.top < 0;
        sticky.dataset.visible = scrolledPast ? 'true' : 'false';
      }
    }, { threshold: 0 });
    io.observe(mainCta);
  }

  #bindEvents() {
    this.addEventListener('click', (e) => {
      const plus = e.target.closest('[data-plus]');
      const minus = e.target.closest('[data-minus]');
      const cta = e.target.closest('[data-cta]');
      const stickyCta = e.target.closest('[data-sticky-cta]');
      if (plus) return this.#onStepper(plus, +1);
      if (minus) return this.#onStepper(minus, -1);
      if (cta || stickyCta) return this.#onCheckout();
    });
  }

  #onStepper(btn, delta) {
    if (btn.disabled) return;
    const row = btn.closest('[data-variant-id]');
    if (!row) return;
    const id = row.dataset.variantId;
    const next = Math.max(0, (this.quantities.get(id) || 0) + delta);
    this.quantities.set(id, next);
    const display = row.querySelector('[data-qty]');
    if (display) {
      if ('value' in display) display.value = String(next);
      else display.textContent = String(next);
    }
    row.classList.toggle('is-selected', next > 0);
    this.refresh();
  }

  #total() {
    let t = 0;
    for (const q of this.quantities.values()) t += q;
    return t;
  }

  #tier(total = this.#total()) {
    if (total >= 9) return 9;
    if (total >= 6) return 6;
    if (total >= 3) return 3;
    return 0;
  }

  refresh() {
    const total = this.#total();
    const tier = this.#tier(total);

    // minus button state per row
    this.querySelectorAll('[data-variant-id]').forEach((row) => {
      const q = this.quantities.get(row.dataset.variantId) || 0;
      const minus = row.querySelector('[data-minus]');
      if (minus) minus.disabled = q <= 0;
    });

    // gift unlocks — toggle .is-unlocked on .bundle-save__gift-image
    this.querySelectorAll('.bundle-save__gift-card').forEach((card) => {
      const key = card.dataset.giftKey;
      const threshold = GIFT_UNLOCKS[key] ?? 0;
      const img = card.querySelector('.bundle-save__gift-image');
      if (!img) return;
      img.classList.toggle('is-unlocked', total >= threshold);
    });

    // Shipping flag tucked under the CTA — counts down to the threshold, flips to "unlocked" at threshold.
    // Hidden entirely when below the minimum order qty (matches the hidden CTA price state).
    const shippingFlag = this.querySelector('[data-shipping-flag]');
    const shippingFlagText = this.querySelector('[data-shipping-flag-text]');
    if (shippingFlag && shippingFlagText) {
      if (total < this.minQuantity) {
        shippingFlag.hidden = true;
      } else if (total < SHIPPING_UNLOCK_AT) {
        const needed = SHIPPING_UNLOCK_AT - total;
        shippingFlagText.innerHTML = `Add <strong>${needed}</strong> more can${needed === 1 ? '' : 's'} and get <strong>FREE</strong> shipping!`;
        shippingFlag.hidden = false;
      } else {
        shippingFlagText.innerHTML = `<strong>FREE</strong> shipping unlocked!`;
        shippingFlag.hidden = false;
      }
    }

    // CTA + price
    const cta = this.querySelector('[data-cta]');
    const ctaText = this.querySelector('[data-cta-text]');
    const priceRow = this.querySelector('[data-price-row]');
    const priceCompare = this.querySelector('[data-price-compare]');
    const priceTotal = this.querySelector('[data-price-total]');
    const teaser = this.querySelector('[data-teaser]');
    const teaserLead = this.querySelector('[data-teaser-lead]');
    const teaserPill = this.querySelector('[data-teaser-pill]');
    const teaserBonus = this.querySelector('[data-teaser-bonus]');
    const savingsBox = this.querySelector('[data-savings-box]');
    const savingsValue = this.querySelector('[data-savings-value]');

    const stickyCta = this.querySelector('[data-sticky-cta]');
    const stickyCtaText = this.querySelector('[data-sticky-cta-text]');

    if (total < this.minQuantity) {
      if (cta) cta.disabled = true;
      if (stickyCta) stickyCta.disabled = true;
      const needed = this.minQuantity - total;
      const disabledText = total === 0
        ? `ADD ${this.minQuantity}+ CANS`
        : `ADD ${needed} MORE CAN${needed === 1 ? '' : 'S'}`;
      if (ctaText) ctaText.textContent = disabledText;
      if (stickyCtaText) stickyCtaText.textContent = disabledText;
      if (priceRow) priceRow.hidden = true;
      if (teaser) teaser.hidden = true;
      if (savingsBox) savingsBox.hidden = true;
      return;
    }

    const perEach = TIER_PRICES[tier] || 0;
    const totalPrice = perEach * total;
    // Retail = maxBag × qty + gift-bundle value at current tier (matches OG retailTotals at tier qty)
    const tierGiftValue = (RETAIL_TOTALS[tier] || 0) - MAX_BAG_PRICE * tier;
    const comparePrice = MAX_BAG_PRICE * total + tierGiftValue;
    const savings = Math.max(0, comparePrice - totalPrice);

    if (cta) cta.disabled = false;
    if (stickyCta) stickyCta.disabled = false;
    if (priceRow) priceRow.hidden = false;
    if (priceCompare) priceCompare.textContent = formatMoney(comparePrice);
    if (priceTotal) priceTotal.textContent = formatMoney(totalPrice);
    if (ctaText) {
      ctaText.innerHTML = `TRY IT NOW · <strong>${formatMoney(totalPrice)}</strong>`;
    }
    if (stickyCtaText) {
      stickyCtaText.innerHTML = `TRY IT NOW · <strong>${formatMoney(totalPrice)}</strong>`;
    }
    if (savingsValue) savingsValue.textContent = formatMoney(savings);
    if (savingsBox) savingsBox.hidden = savings <= 0;

    // Teaser: "ADD N MORE CANS TO GET" [PILL: X% OFF TODAY] [BLACK PILL: + 4 FREE GIFTS]
    // Pill is red at the 55% tier, blue at the 59% tier.
    if (teaser && teaserLead && teaserPill && teaserBonus) {
      let leadText = '';
      let pillText = '';
      let bonusText = '';
      let pillBlue = false;
      if (total < 6) {
        const needed = 6 - total;
        leadText = `Add ${needed} more to get`;
        pillText = '55% OFF TODAY';
        bonusText = '+ 4 FREE GIFTS';
      } else if (total < 9) {
        const needed = 9 - total;
        leadText = `Add ${needed} more to get`;
        pillText = '59% OFF TODAY';
        bonusText = '+ 4 FREE GIFTS';
        pillBlue = true;
      } else {
        leadText = "You're getting";
        pillText = '59% OFF TODAY';
        bonusText = '+ 4 FREE GIFTS';
        pillBlue = true;
      }
      if (leadText) {
        teaserLead.textContent = leadText;
        teaserPill.textContent = pillText;
        teaserBonus.textContent = bonusText;
        teaserPill.classList.toggle('bsq1__teaser-pill--blue', pillBlue);
        teaser.hidden = false;
      } else {
        teaser.hidden = true;
      }
    }
  }

  async #onCheckout() {
    const total = this.#total();
    if (total < this.minQuantity) return;

    this.#showLoadingState();

    const paidItems = [];
    for (const [variantId, qty] of this.quantities.entries()) {
      if (qty <= 0) continue;
      const item = { id: Number(variantId), quantity: qty };
      if (this.sellingPlanId) item.selling_plan = Number(this.sellingPlanId);
      paidItems.push(item);
    }

    // Add unlocked free-gift products (one-time, no selling plan)
    const items = [...paidItems];
    for (const [key, threshold] of Object.entries(GIFT_UNLOCKS)) {
      if (total < threshold) continue;
      const giftId = GIFT_VARIANT_IDS[key];
      if (!giftId) continue; // e.g. 'shipping' isn't a product
      items.push({ id: Number(giftId), quantity: 1 });
    }

    const addToCart = (cartItems) => fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: cartItems }),
    });

    try {
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
      }).catch(() => { /* non-fatal */ });

      let res = await addToCart(items);
      // Free gifts must never block a paid order: if a gift is unavailable (422), retry paid items only.
      if (res.status === 422 && paidItems.length > 0 && paidItems.length < items.length) {
        res = await addToCart(paidItems);
      }
      if (!res.ok) throw new Error(`Add to cart failed: ${res.status}`);
      // Redirect through the discount URL so shipping gift applies automatically.
      window.location.href = CHECKOUT_DISCOUNT_CODE
        ? `/discount/${encodeURIComponent(CHECKOUT_DISCOUNT_CODE)}?redirect=/checkout`
        : '/checkout';
    } catch (err) {
      console.error('[bundle-save-qty-1-step] checkout failed', err);
      this.#hideLoadingState();
      this.refresh();
      alert('Could not add to cart. Please try again.');
    }
  }
}

if (!customElements.get('bundle-save-qty-1-step-component')) {
  customElements.define('bundle-save-qty-1-step-component', BundleSaveQty1StepComponent);
}
